import { Auth, CognitoUser } from "utils/cognito-auth";
import { CreateOrganisationBody } from "components/user/sign-in/onboarding-wizard";
import { UserMembership } from "data/models";
import { fetchUserMemberships } from "data/queries-common";
import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useCookies } from "react-cookie";
import { IntercomProps } from "react-use-intercom";
import { Either, left, right } from "utils";
import { makeApiRequestPostToUrl, setCurrentOrgId } from "utils/api-client";
import type { CookieSetOptions } from "universal-cookie";
import { OrganisationRow } from "./models";

export type SignOutOpts = { global?: boolean };

export interface User {
  firstName: string;
  lastName: string;
  email: string;
  cognitoSub: string;
  cognitoUser: CognitoUser;
  isAdmin: boolean;
  isSales: boolean;
  organisations: Organisation[];
  activeOrg: Organisation | null;
  phoneNumber: string;
  role: string;
  workPhone: string;
  emailVerified: boolean;
  phoneNumberVerified: boolean;
  address: string;
  onboardingComplete: string;
}

export interface Organisation {
  name: string;
  organisationId: string;
  active: boolean;
  abn?: string;
  subscription?: string;
  hasAccess?: boolean;
}

// What the app should show — completely separate from who the user is
export type AppPhase =
  | "loading"
  | "unauthenticated"
  | "onboarding" // first login, no accepted orgs
  | "pending-invite" // has orgs but none accessible, or waiting on invite
  | "no-access" // has accepted orgs, all have hasAccess=false
  | "app"; // normal authenticated state

export interface IdentityContext {
  user: User | null;
  phase: AppPhase;
  refreshIdentity: () => Promise<void>;
  setPhase: Dispatch<SetStateAction<AppPhase>>;
  signOut: (opts?: SignOutOpts) => Promise<void>;
  switchOrg: (org: Organisation) => void;
}

// Keep the old IdentityState type as an alias for backwards compat with the
// many callers that just want to check if we have a user
export type IdentityState = User | false | "loading";

export const switchedOrganisationCookieName = "switched-organisation";

const IdentityCtx = createContext<IdentityContext>({
  user: null,
  phase: "loading",
  refreshIdentity: async () => {},
  setPhase: () => {},
  signOut: async () => {},
  switchOrg: () => {},
});

export const useIdentityContext = () => useContext(IdentityCtx);

// Legacy hook — returns [IdentityState, setter] for backwards compat with
// all the existing callers. New code should prefer useIdentityContext().
export function useIdentity(): [
  IdentityState,
  Dispatch<SetStateAction<IdentityState>>,
] {
  const ctx = useIdentityContext();

  const identityState: IdentityState =
    ctx.phase === "loading"
      ? "loading"
      : ctx.user === null
        ? false
        : ctx.user;

  // The legacy setter only handles the sign-out case (setUserState(false))
  // and full user replacement. Map them onto the new primitives.
  const legacySetter: Dispatch<SetStateAction<IdentityState>> = (action) => {
    const next =
      typeof action === "function" ? action(identityState) : action;
    if (next === false) {
      ctx.setPhase("unauthenticated");
    } else if (next === "loading") {
      ctx.setPhase("loading");
    } else {
      // A full User replacement — derive phase from the new user and refresh
      ctx.refreshIdentity();
    }
  };

  return [identityState, legacySetter];
}

// Helpers preserved for callers that use them
export const GetUserIfLoggedIn = (
  identityState: IdentityState,
): User | false => {
  if (identityState === "loading" || identityState === false) {
    return false;
  }
  return identityState;
};

export const isAdminIdentity = (identityState: IdentityState): boolean => {
  const user = GetUserIfLoggedIn(identityState);
  return user ? user.isAdmin : false;
};

export const isSalesIdentity = (identityState: IdentityState): boolean => {
  const user = GetUserIfLoggedIn(identityState);
  return user ? user.isSales : false;
};

export const getActiveOrganisation = (
  identityState: IdentityState,
): Organisation | false => {
  const user = GetUserIfLoggedIn(identityState);
  if (!user) return false;
  return user.activeOrg ?? false;
};

// UserContext kept for the handful of components that use it directly
export const UserContext = createContext<
  [IdentityState, Dispatch<SetStateAction<IdentityState>> | undefined]
>([false, undefined]);

const derivePhase = (user: User): AppPhase => {
  // Wizard hasn't been completed yet — stay in onboarding regardless of orgs.
  // Prevents an accepted invite mid-wizard from bumping the user straight
  // into the app and skipping MFA setup.
  if (user.onboardingComplete !== "true") {
    return "onboarding";
  }
  if (user.organisations.length === 0) {
    return "pending-invite";
  }
  const hasAccessibleOrg = user.organisations.some((o) => o.hasAccess !== false);
  if (!hasAccessibleOrg) {
    return "no-access";
  }
  return "app";
};

const buildUser = (
  cognitoUser: CognitoUser,
  memberships: UserMembership[],
  savedOrg?: Organisation,
): User => {
  const attributes = cognitoUser.attributes ?? {};
  const groups: string[] =
    (cognitoUser.getSignInUserSession()?.getAccessToken().payload?.[
      "cognito:groups"
    ] as string[]) ?? [];

  const allAcceptedOrgs: Organisation[] = memberships
    .filter((m) => m.status === "Accepted")
    .map((m) => ({
      name: m.organisationName,
      organisationId: m.organisationId,
      active: false,
      subscription: m.subscription,
      hasAccess: m.hasAccess !== false,
    }));

  const orgs = setActiveOrganisation(allAcceptedOrgs, savedOrg);
  const activeOrg = orgs.find((o) => o.active) ?? null;

  return {
    firstName: attributes.given_name,
    lastName: attributes.family_name,
    email: attributes.email,
    cognitoSub: attributes.sub,
    cognitoUser,
    isAdmin: groups.includes("admin"),
    isSales: groups.includes("sales"),
    organisations: orgs,
    activeOrg,
    onboardingComplete: attributes["custom:onboardingComplete"] ?? "",
    phoneNumber: attributes.phone_number,
    role: attributes["custom:role"],
    workPhone: attributes["custom:work_phone"],
    phoneNumberVerified: attributes.phone_number_verified === "true",
    emailVerified: attributes.email_verified === "true",
    address: attributes.address,
  };
};

export const IdentityProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [phase, setPhase] = useState<AppPhase>("loading");

  const [cookies, setCookies, removeCookieRaw] = useCookies([
    switchedOrganisationCookieName,
  ]);

  const organisationCookie: Organisation | undefined =
    cookies[switchedOrganisationCookieName];

  // Keep org ID module variable in sync with active org — runs during render
  // so it's set before any child queries fire
  const activeOrgId = user?.activeOrg?.organisationId ?? "";
  setCurrentOrgId(activeOrgId);

  const loadIdentity = async () => {
    try {
      const cognitoUser = await Auth.currentAuthenticatedUser();
      const memberships = await fetchUserMemberships(true);
      const newUser = buildUser(cognitoUser, memberships, organisationCookie);
      setUser(newUser);
      setPhase(derivePhase(newUser));
    } catch {
      setUser(null);
      setPhase("unauthenticated");
    }
  };

  // Load identity on mount
  useEffect(() => {
    loadIdentity();
  }, []);

  // Persist active org to cookie whenever it changes
  useEffect(() => {
    if (user?.activeOrg) {
      if (user.activeOrg.organisationId === organisationCookie?.organisationId) {
        return;
      }
      setCookies(switchedOrganisationCookieName, user.activeOrg, {
        path: "/",
      });
    }
  }, [user?.activeOrg?.organisationId]);

  const handleSignOut = async (opts?: SignOutOpts) => {
    try {
      await Auth.signOut(opts);
    } catch {
      // proceed with local cleanup even if signOut throws
    }
    removeCookieRaw(switchedOrganisationCookieName);
    setCurrentOrgId("");
    setUser(null);
    setPhase("unauthenticated");
  };

  const switchOrg = (org: Organisation) => {
    if (!user) return;
    if (org.hasAccess === false) return;

    const updatedOrgs = user.organisations.map((o) => ({
      ...o,
      active: o.organisationId === org.organisationId,
    }));
    const activeOrg = updatedOrgs.find((o) => o.active) ?? null;
    setUser({ ...user, organisations: updatedOrgs, activeOrg });
  };

  const ctx: IdentityContext = {
    user,
    phase,
    refreshIdentity: loadIdentity,
    setPhase,
    signOut: handleSignOut,
    switchOrg,
  };

  return (
    <IdentityCtx.Provider value={ctx}>
      {children}
    </IdentityCtx.Provider>
  );
};

export type Props = {
  children: ReactNode;
  context: IdentityState;
};

// ---- Functions used directly by callers outside the context ----

export const signOut = async (
  removeCookie: (
    name: typeof switchedOrganisationCookieName,
    options?: CookieSetOptions,
  ) => void,
  opts?: SignOutOpts,
): Promise<Either<boolean, Error>> => {
  try {
    await Auth.signOut(opts);
    removeCookie(switchedOrganisationCookieName);
    return left(true);
  } catch (error) {
    return right(error instanceof Error ? error : new Error(String(error)));
  }
};

export const switchOrganisation = async (
  organisation: Organisation,
  identityState: IdentityState,
  setUserState: Dispatch<SetStateAction<IdentityState>>,
  update: (props?: Partial<IntercomProps>) => void,
) => {
  const user = GetUserIfLoggedIn(identityState);
  if (!user) return;
  if (organisation.hasAccess === false) return;

  update({
    company: {
      companyId: organisation.organisationId,
      name: organisation.name,
    },
    customAttributes: { current_organisation: organisation.name },
  });

  const updatedOrgs = setActiveOrganisation(user.organisations, organisation);
  const activeOrg = updatedOrgs.find((o) => o.active) ?? null;
  setUserState({ ...user, organisations: updatedOrgs, activeOrg });
};

export const userCreateOrganisation = async (
  createOrg: CreateOrganisationBody,
): Promise<OrganisationRow> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/v2/organisation`,
      "POST",
      createOrg,
    );
    const responseData = await response.json();
    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }
    return responseData;
  } catch (err) {
    throw new Error(`Failed to create organisation: ${err}`);
  }
};

export async function isAdmin() {
  const cognitoUser = await Auth.currentAuthenticatedUser();
  const groups: string[] =
    (cognitoUser.getSignInUserSession()?.getAccessToken().payload?.[
      "cognito:groups"
    ] as string[]) ?? [];
  return groups.includes("admin");
}

const setActiveOrganisation = (
  organisations: Organisation[],
  organisation?: Organisation,
): Organisation[] => {
  if (!organisation) {
    const orgs = organisations.map((o) => ({ ...o, active: false }));
    const firstAccessibleIndex = orgs.findIndex((o) => o.hasAccess !== false);
    if (firstAccessibleIndex >= 0) {
      orgs[firstAccessibleIndex].active = true;
    }
    return orgs;
  }

  const orgs = organisations.map((o) => ({
    ...o,
    active: o.organisationId === organisation.organisationId && o.hasAccess !== false,
  }));

  if (!orgs.find((o) => o.active)) {
    const firstAccessibleIndex = orgs.findIndex((o) => o.hasAccess !== false);
    if (firstAccessibleIndex >= 0) {
      orgs[firstAccessibleIndex].active = true;
    }
  }

  return orgs;
};
